import React, { createContext, useContext, useMemo, useState } from "react";

import { useQueries, useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { Options } from "../models/access";
import { Agent } from "../models/agent";
import { Branch } from "../models/branch";
import { Destination } from "../models/destination";
import { Fleet } from "../models/fleet";
import { Instruction } from "../models/instruction";
import { Item } from "../models/item";
import { JobMode } from "../models/job.mode";
import { JobType } from "../models/jobType";
import { Languages } from "../models/languages";
import { LeadSource } from "../models/leadsource";
import { PaymentMethod } from "../models/payment.method";
import { RoomJob } from "../models/sales";
import { ServiceBooking } from "../models/service.booking";
import { Staff } from "../models/staff";
import { Statuses } from "../models/statuses";
import { TaskType as ITaskType } from "../models/task.type";
import { Timezone } from "../models/timezone";
import { User } from "../models/user";
import AccessService from "../services/access.service";
import AgentService from "../services/agent.service";
import BranchService from "../services/branch.service";
import DestinationService from "../services/destination.service";
import FleetService from "../services/fleet.service";
import InstructionService from "../services/instruction.service";
import ItemsService from "../services/items.service";
import JobTypeService from "../services/job.type.service";
import LanguagesService from "../services/languages.service";
import LeadSourceFromService from "../services/lead.source.from.service";
import MaterialsService from "../services/materials.service";
import ModeJobService from "../services/mode.job.service";
import PaymentMethodService from "../services/payment.method.service";
import RoomService from "../services/room.service";
import StaffService from "../services/staff.service";
import StatusesService from "../services/statuses.service";
import TaskService from "../services/task.service";
import TimezoneService from "../services/timezone.service";
import UserService from "../services/user.service";
import { LanguagesUsage, Status, SubStatusUsedBy, UserTypes } from "../util/enums";
import { useAppState } from "./StateContext";

export type ExternalList = {
  branchList?: Branch[];
  jobTypeList?: JobType[];
  statusList?: Statuses[];
  leadSourceFromList?: LeadSource[];
  jobModeList?: JobMode[];
  userList?: User[];
  timezoneList?: Timezone[];
  destinationList?: Destination[];
  roomList?: RoomJob[];
  itemList?: Array<Item | ServiceBooking>;
  materialList?: ServiceBooking[];
  taskTypeList?: ITaskType[];
  drivewayLengthList?: Options[];
  drivewayList?: Options[];
  parkingList?: Options[];
  carryDistanceList?: Options[];
  accessActionList?: Options[];
  instructionList?: Instruction[];
  agentList?: Agent[];
  staffList?: Staff[];
  fleetList?: Fleet[];
  paymentMethodList?: PaymentMethod[];
  languages?: Languages[];
};

interface MovingResourcesContextProps extends ExternalList {
  loading: boolean;
  loadingDetailsResources: boolean;
}

const MovingResourcesContext = createContext<MovingResourcesContextProps>(
  {} as MovingResourcesContextProps
);

interface MovingResourcesProviderProps {
  children: React.ReactChild;
  type?: SubStatusUsedBy;
}

type Request = Array<[queryKey: string, execute: Function, ...executeArgs: any, enabled?: boolean]>;

const MovingResourcesProvider = ({
  children,
  type = SubStatusUsedBy.ESTIMATES,
}: MovingResourcesProviderProps) => {
  const { currentUser } = useAppState();

  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [paymentMethodList, setPaymentMethodList] = useState<PaymentMethod[]>([]);

  const [allAgents, setAllAgents] = useState<Agent[]>([]);

  // Do not change the order of requests, when adding or removing, check the destructuring
  const Resources: Request = useMemo(() => {
    return [
      ["branch.getAll", BranchService.getAll, { sortBy: JSON.stringify([["name", "ASC"]]) }],
      [
        "statuses.getAll",
        StatusesService.getAll,
        { usedBy: type, sortBy: JSON.stringify([["id", "ASC"]]) },
      ],
      ["job.type.getAll", JobTypeService.getAll, { sortBy: JSON.stringify([["name", "asc"]]) }],
      [
        "lead.source.from.getAll",
        LeadSourceFromService.getAll,
        { sortBy: JSON.stringify(["name"]) },
      ],
      ["mode.job.getAll", ModeJobService.getAll],
      ["destination.getAll", DestinationService.getAll, { sortBy: JSON.stringify(["name"]) }],
      ["room.getAll", RoomService.getAllRoomJob, { orderBy: JSON.stringify(["name"]), jobId: "" }],
      [
        "items.getAll",
        ItemsService.getAll,
        {
          include: JSON.stringify([{ association: "rooms" }, { association: "packingMaterials" }]),
          orderBy: JSON.stringify(["name"]),
        },
      ],
      ["materials.getAll", MaterialsService.getAll, { orderBy: JSON.stringify(["name"]), include: JSON.stringify([{ association: "rooms", attributes: ["id", "name", "code"] }]) }],
      ["task.type.getAll", TaskService.types, { sortBy: JSON.stringify(["sequence"]) }],
      [
        "driveway.length.getAll",
        AccessService.getAllDrivewayLengths,
        { sortBy: JSON.stringify([["id", "ASC"]]) },
      ],
      ["parking.getAll", AccessService.getAllParkings],
      ["carry.distance.getAll", AccessService.getAllCarryDistances],
      ["access.action.getAll", AccessService.getAllActions],
      ["driveway.getAll", AccessService.getAllDriveways],
      [
        "instruction.getAll",
        InstructionService.getAll,
        { sortBy: JSON.stringify(["description"]) },
      ],
      ["fleet.getAll", FleetService.getAll, { outOfService: 0 }, type === SubStatusUsedBy.ORDERS],
      ["staff.getAll", StaffService.getAll, {}, type === SubStatusUsedBy.ORDERS],
      [
        "languages.getAll",
        LanguagesService.getAll,
        { usage: LanguagesUsage.WEB, sortBy: JSON.stringify([["name", "asc"]]) },
      ],
    ];
  }, [type]);

  const results = useQueries({
    queries: Resources.map(([queryKey, execute, args, enabled = true]) => {
      return {
        queryKey: [queryKey, currentUser?.companyId, type],
        queryFn: () =>
          execute({
            companyId: currentUser?.companyId,
            status: Status.ACTIVE,
            ...args,
          }),
        enabled: !!currentUser?.companyId && enabled,
        refetchOnWindowFocus: false,
        staleTime: 2 * 60 * 1000, // 2 minutes,
        cacheTime: 1 * 60 * 60 * 1000, // 1 hour,
      };
    }),
  });

  const [
    resultBranch,
    resultStatuses,
    resultJobType,
    resultLeadSource,
    resultModeJob,
    resultDestination,
    resultRoom,
    resultItem,
    resultMaterial,
    resultTaskType,
    resultDrivewayLength,
    resultParking,
    resultCarryDistance,
    resultAccessAction,
    resultDriveway,
    resultInstruction,
    resultFleet,
    resultStaff,
    resultLanguages,
  ] = results;

  const loadingQueries = results.some((result) => result.isFetching);

  const detailsResources = [
    resultBranch,
    resultStatuses,
    resultJobType,
    resultLeadSource,
    resultModeJob,
    resultLanguages,
  ];

  const loadingDetailsResources = detailsResources.some((result) => result.isFetching);

  const { isLoading: loadingUsers } = useQuery(
    ["users.getAll.moving"],
    () =>
      UserService.getAll({
        attributes: JSON.stringify([
          "id",
          "name",
          "lastName",
          "fullName",
          "typeUser",
          "code",
          "email",
          "phone",
          "companyId",
          "status",
        ]),
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            include: [
              {
                association: "manager",
                attributes: [
                  "id",
                  "name",
                  "lastName",
                  "fullName",
                  "typeUser",
                  "code",
                  "email",
                  "phone",
                  "companyId",
                ],
              },
            ],
            where: {
              status: Status.ACTIVE,
              typeUser: [UserTypes.ADMIN, UserTypes.USER, UserTypes.SALES],
              companyId: currentUser?.companyId,
            },
          },
        ]),
        sortBy: JSON.stringify(["name"]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setAllUsers(response.data);
      },
    }
  );

  const { data: timezones, isLoading: loadingTimezones } = useQuery(
    ["moving.resource.timezones.list"],
    async () => {
      const response = await TimezoneService.getAll({
        sortBy: JSON.stringify(["gmtOffset"]),
      });

      if (response && response?.data instanceof Array) {
        return response?.data;
      }

      return [];
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60 * 60 * 1000, // 1 hour,
      cacheTime: 5 * 60 * 60 * 1000, // 5 hours,
    }
  );

  const { isLoading: loadingAgents } = useQuery(
    ["agents.list"],
    () =>
      AgentService.getAll({
        createdCompanyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        status: Status.ACTIVE,
        include: JSON.stringify([
          { association: "locations" },
          {
            association: "services",
            required: false,
            where: { status: Status.ACTIVE },
            include: [{ association: "service" }],
          },
          { association: "associatedCompany" },
        ]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllAgents(response.data);
      },
    }
  );

  const { isLoading: loadingPaymentMethod } = useQuery(
    ["payment.methods.list"],
    () =>
      PaymentMethodService.getAll({
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["id", "asc"]]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setPaymentMethodList(response.data);
        }
      },
    }
  );

  const isLoading =
    loadingQueries || loadingUsers || loadingTimezones || loadingAgents || loadingPaymentMethod;

  const getDataFromResult = (result: UseQueryResult<AxiosResponse, unknown>) => {
    if (result && result?.data?.data && result?.data?.data instanceof Array) {
      return result?.data?.data;
    }

    return [];
  };

  return (
    <MovingResourcesContext.Provider
      value={{
        branchList: getDataFromResult(resultBranch),
        userList: allUsers,
        jobModeList: getDataFromResult(resultModeJob),
        jobTypeList: getDataFromResult(resultJobType),
        leadSourceFromList: getDataFromResult(resultLeadSource),
        statusList: getDataFromResult(resultStatuses),
        timezoneList: timezones,
        destinationList: getDataFromResult(resultDestination),
        roomList: getDataFromResult(resultRoom),
        itemList: getDataFromResult(resultItem),
        materialList: getDataFromResult(resultMaterial),
        taskTypeList: getDataFromResult(resultTaskType),
        accessActionList: getDataFromResult(resultAccessAction),
        carryDistanceList: getDataFromResult(resultCarryDistance),
        drivewayLengthList: getDataFromResult(resultDrivewayLength),
        drivewayList: getDataFromResult(resultDriveway),
        parkingList: getDataFromResult(resultParking),
        instructionList: getDataFromResult(resultInstruction),
        languages: getDataFromResult(resultLanguages),
        agentList: allAgents,
        staffList: getDataFromResult(resultStaff),
        fleetList: getDataFromResult(resultFleet),
        paymentMethodList,
        loading: isLoading,
        loadingDetailsResources,
      }}
    >
      {children}
    </MovingResourcesContext.Provider>
  );
};

export { MovingResourcesContext, MovingResourcesProvider };

export const useMovingResources = () => {
  const context = useContext(MovingResourcesContext);

  return context;
};
